import type { ComponentProps, HTMLAttributes, ReactElement } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';

import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

import { BomAutocomplete } from '../BomAutocomplete';

import { SizeValue } from './SizeValue';

type MultipleType = false; // We can never select multiple sizes in a field, so this is false
type DisableClearableType = boolean; // The field is clearable
type FreeSoloType = false; // We do not allow free text input, the users must select a real value from the list

const placeholder = 'Select size';

const renderOptionFn = (
    props: HTMLAttributes<HTMLLIElement>,
    size: string,
    _state: AutocompleteRenderOptionState
): ReactElement | null => {
    if (!size) return null;

    return (
        <SizeValue
            {...props}
            value={size}
        />
    );
};

interface SizeAutocompleteProps
    extends Partial<
        Omit<
            ComponentProps<
                typeof BomAutocomplete<
                    string,
                    MultipleType,
                    DisableClearableType,
                    FreeSoloType
                >
            >,
            'options' | 'renderInput' | 'value'
        >
    > {
    value: string | null;
    options: Array<string>;
    onIntersection?: () => void;
}

const SizeAutocomplete = forwardRef(
    (
        {
            value,
            options,
            onIntersection,
            ...autoCompleteProps
        }: SizeAutocompleteProps,
        ref
    ): ReactElement => {
        const isClearable = options.length !== 1;
        const inputRef = useRef<HTMLInputElement>(null); // Ref for the input element to manage focus and blur.

        // Hide the popup indicator only when there is nothing to choose from:
        // no options at all, or exactly one option that is already selected.
        // When there is one option but the current value doesn't match it (stale/invalid value),
        // we still show the arrow so the user can open the dropdown and correct their selection.
        const shouldHidePopupIndicator =
            options.length === 0 ||
            (options.length === 1 && value === options[0]);

        const isInteractive =
            !shouldHidePopupIndicator && !autoCompleteProps.disabled;
        const nativeTabIndex = isInteractive ? 0 : -1;

        // This observer is used to fetch more options when the last option comes into view
        const observer = useRef<IntersectionObserver | null>(null);
        const lastSizeRef = useCallback(
            (node: HTMLLIElement | null) => {
                // Disconnect the previous IntersectionObserver instance if it exists
                if (observer.current) observer.current.disconnect();

                // Create a new IntersectionObserver instance
                observer.current = new IntersectionObserver((entries) => {
                    // When the last option is intersecting the viewport, trigger callback
                    if (entries[0].isIntersecting) {
                        onIntersection?.();
                    }
                });

                // Observe the last option
                if (node) observer.current.observe(node);
            },
            [onIntersection]
        );

        useEffect(() => {
            if (inputRef.current) {
                inputRef.current.tabIndex = nativeTabIndex;
            }
        }, [nativeTabIndex, value, options.length]);

        return (
            <BomAutocomplete<
                string,
                MultipleType,
                DisableClearableType,
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
                FreeSoloType
            >
                {...autoCompleteProps}
                ref={ref}
                value={value}
                options={options}
                noOptionsText="No sizes found"
                disableClearable={!isClearable}
                slotProps={{
                    popupIndicator: shouldHidePopupIndicator
                        ? { style: { display: 'none' } }
                        : {},
                }}
                textfieldProps={{
                    placeholder,
                    inputRef,
                }}
                renderOption={(props, option, state) =>
                    renderOptionFn(
                        {
                            ...props,
                            // @ts-expect-error - Object literal may only specify known properties, and 'ref' does not exist in type 'HTMLAttributes<HTMLLIElement>'.
                            ref:
                                // @ts-expect-error - Element implicitly has an 'any' type because expression of type '"data-option-index"' can't be used to index type 'HTMLAttributes<HTMLLIElement> & { key: any; }'. Property 'data-option-index' does not exist on type 'HTMLAttributes<HTMLLIElement> & { key: any; }'.

                                props['data-option-index'] ===
                                options.length - 1
                                    ? lastSizeRef
                                    : null,
                        },
                        option,
                        state
                    )
                }
                renderValue={(valueToRender) => (
                    <SizeValue
                        value={valueToRender}
                        disabled={autoCompleteProps.disabled}
                    />
                )}
            />
        );
    }
);

SizeAutocomplete.displayName = 'SizeAutocomplete';

export { SizeAutocomplete };
export type { SizeAutocompleteProps };
